import React, { useState } from 'react';
import { Filter, Grid, List, Search } from 'lucide-react';
import GameCard from './GameCard';
import { 
  GAME_CATEGORIES, 
  SUBSCRIPTION_TIERS,
  getSubscriptionTierName,
  getGamesByCategory 
} from '../../constants/games';

const GameGrid = ({ 
  games = [], 
  userSubscription = SUBSCRIPTION_TIERS.FREE,
  onCreateGame,
  isLoading = false,
  className = ""
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Filter games based on selected filters
  const filteredGames = games.filter(game => {
    const matchesCategory = selectedCategory === 'all' || game.category === selectedCategory;
    const matchesTier = selectedTier === 'all' || game.subscriptionTier === selectedTier;
    const matchesSearch = game.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         game.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesTier && matchesSearch;
  });

  // Get unique categories from games
  const availableCategories = ['all', ...new Set(games.map(game => game.category))];
  
  // Get unique subscription tiers from games
  const availableTiers = ['all', ...new Set(games.map(game => game.subscriptionTier))];

  const categoryNames = {
    [GAME_CATEGORIES.CLASSIC]: 'Classic',
    [GAME_CATEGORIES.PUZZLE]: 'Puzzle',
    [GAME_CATEGORIES.STRATEGY]: 'Strategy',
    [GAME_CATEGORIES.WORD]: 'Word',
    [GAME_CATEGORIES.MATH]: 'Math',
    [GAME_CATEGORIES.CARD]: 'Card'
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-discord-light" />
            <input
              type="text"
              placeholder="Search games..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input input-bordered w-full pl-10 bg-discord-darker border-discord-dark text-discord-lightest"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-discord-light" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="select select-bordered bg-discord-darker border-discord-dark text-discord-lightest"
            >
              <option value="all">All Categories</option>
              {availableCategories.slice(1).map(category => (
                <option key={category} value={category}>
                  {categoryNames[category] || category}
                </option>
              ))}
            </select>
          </div>

          {/* Tier Filter */}
          <div className="flex items-center gap-2">
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="select select-bordered bg-discord-darker border-discord-dark text-discord-lightest"
            >
              <option value="all">All Tiers</option>
              {availableTiers.slice(1).map(tier => (
                <option key={tier} value={tier}>
                  {getSubscriptionTierName(tier)}
                </option>
              ))}
            </select>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'}`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex justify-between items-center">
        <p className="text-discord-light">
          Showing {filteredGames.length} of {games.length} games
        </p>
        <div className="flex gap-2">
          {selectedCategory !== 'all' && (
            <span className="badge badge-primary">
              {categoryNames[selectedCategory] || selectedCategory}
            </span>
          )}
          {selectedTier !== 'all' && (
            <span className="badge badge-secondary">
              {getSubscriptionTierName(selectedTier)}
            </span>
          )}
          {searchTerm && (
            <span className="badge badge-outline">
              "{searchTerm}"
            </span>
          )}
        </div>
      </div>

      {/* Games Grid/List */}
      {filteredGames.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-6xl mb-4">🎮</div>
          <h3 className="text-xl font-bold text-discord-lightest mb-2">
            No games found
          </h3>
          <p className="text-discord-light">
            Try adjusting your filters or search term
          </p>
        </div>
      ) : (
        <div className={`${
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
            : 'space-y-4'
        }`}>
          {filteredGames.map((game) => (
            <GameCard
              key={game.id}
              game={game}
              userSubscription={userSubscription}
              onCreateGame={onCreateGame}
              isLoading={isLoading}
              className={viewMode === 'list' ? 'card-side' : ''}
            />
          ))}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="loading loading-spinner loading-lg"></div>
        </div>
      )}
    </div>
  );
};

export default GameGrid;
